import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, Circle } from 'react-leaflet';
import { MapPinIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

/**
 * LocationPicker Component
 * Interactive map component for selecting property location with GPS or manual pin drop
 */
const LocationPicker = ({
  onLocationSelect,
  initialLat = 8.7139,
  initialLng = 77.7567,
  showRadius = false,
  radiusKm = 5,
  disabled = false
}) => {
  const [position, setPosition] = useState([initialLat, initialLng]);
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);

  // Update position when initial coordinates change
  useEffect(() => {
    if (initialLat && initialLng) {
      setPosition([initialLat, initialLng]);
    }
  }, [initialLat, initialLng]);

  // Get current location using browser GPS
  const getCurrentLocation = useCallback(() => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setPosition([lat, lng]);
          onLocationSelect({ latitude: lat, longitude: lng });
          reverseGeocode(lat, lng);
          setLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Unable to get your location. Please select manually on the map.');
          setLoading(false);
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
      setLoading(false);
    }
  }, [onLocationSelect]);

  // Reverse geocode to get address from coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'Ducktail-Property-App'
          }
        }
      );
      const data = await response.json();
      if (data.display_name) {
        setAddress(data.display_name);
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    }
  };

  // Map click handler component
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        if (!disabled) {
          const { lat, lng } = e.latlng;
          setPosition([lat, lng]);
          onLocationSelect({ latitude: lat, longitude: lng });
          reverseGeocode(lat, lng);
        }
      },
    });
    return null;
  };

  // Component to update map view when position changes
  const ChangeView = ({ center }) => {
    const map = useMapEvents({});
    useEffect(() => {
      map.setView(center, map.getZoom());
    }, [center, map]);
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Location Info */}
      {address && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <MapPinIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900">Selected Location</p>
              <p className="text-xs text-blue-700 mt-1">{address}</p>
              <p className="text-xs text-blue-600 mt-1">
                Coordinates: {position[0].toFixed(6)}, {position[1].toFixed(6)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* GPS Button */}
      {!disabled && (
        <button
          type="button"
          onClick={getCurrentLocation}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Getting your location...
            </>
          ) : (
            <>
              <MapPinIcon className="w-5 h-5" />
              Use My Current Location
            </>
          )}
        </button>
      )}

      {/* Map Container */}
      <div className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-sm">
        <MapContainer
          center={position}
          zoom={13}
          style={{ height: '400px', width: '100%' }}
          scrollWheelZoom={true}
        >
          <ChangeView center={position} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {!disabled && <MapClickHandler />}
          <Marker position={position} icon={customIcon} />
          {showRadius && (
            <Circle
              center={position}
              radius={radiusKm * 1000} // Convert km to meters
              pathOptions={{
                color: '#3B82F6',
                fillColor: '#3B82F6',
                fillOpacity: 0.1,
                weight: 2,
                dashArray: '5, 5'
              }}
            />
          )}
        </MapContainer>
      </div>

      {/* Helper Text */}
      {!disabled && (
        <p className="text-xs text-gray-500 text-center">
          📍 Click anywhere on the map to pin your property location, or use GPS for auto-detection
        </p>
      )}
    </div>
  );
};

export default LocationPicker;